import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ArrowRight, ChevronDown } from 'lucide-react';
import LanguageSelector from '../LanguageSelector';
import { Button } from './primitives';
import { BOOKING_URL, EMAIL_TOOL_URL, SECTION_IDS } from './constants';
import useGoogleAnalytics from '../../hooks/useGoogleAnalytics';

const NAV_LINKS = [
  { key: 'solutions', anchor: SECTION_IDS.services },
  { key: 'technologies', anchor: SECTION_IDS.technologies },
  { key: 'work', anchor: SECTION_IDS.marketplace },
];

// Free Tools dropdown entries. `route` items navigate within the app,
// `external` items open in a new tab.
const FREE_TOOLS = [
  { key: 'email_generator', href: EMAIL_TOOL_URL, external: true },
  { key: 'new_to_zendesk', route: '/started-guide' },
  { key: 'sla_simulator', route: '/sla-simulator' },
];

// Zendesk tools dropdown entries (paid resources hosted as in-app routes).
const PRODUCTS = [
  { key: 'app_builder', route: '/app-builder' },
  { key: 'workbook', route: '/zendesk-workbook' },
  { key: 'action_flows', route: '/zendesk-action-flows' },
  { key: 'ocr', route: '/zendesk-ocr' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const { events: gaEvents } = useGoogleAnalytics();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isHome = pathname === '/';
  const [mobileOpen, setMobileOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Scroll to a homepage section. From sub-pages, navigate home first and
  // pass the target so HomePage can scroll once mounted.
  const goToSection = (anchor) => (e) => {
    e.preventDefault();
    setMobileOpen(false);
    if (isHome) {
      const el = document.getElementById(anchor);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate('/', { state: { scrollTo: anchor } });
    }
  };

  // Navigate to an in-app route (used by Free Tools + App Builder).
  const goRoute = (route) => (e) => {
    e.preventDefault();
    setMobileOpen(false);
    setToolsOpen(false);
    setProductsOpen(false);
    navigate(route);
  };

  const goHome = (e) => {
    e.preventDefault();
    setMobileOpen(false);
    if (isHome) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const solid = scrolled || !isHome;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid ? 'bg-ink/80 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 sm:px-6 lg:px-8">
        {/* Wordmark */}
        <a href="/" onClick={goHome} className="flex items-center gap-2">
          <span className="text-xl font-bold tracking-tight text-white">
            Easy<span className="bg-accent-gradient bg-clip-text text-transparent">App</span>
          </span>
        </a>

        {/* Desktop links */}
        <div className="hidden items-center gap-1 md:flex">
          {NAV_LINKS.map((link) => (
            <a
              key={link.key}
              href={`/#${link.anchor}`}
              onClick={goToSection(link.anchor)}
              className="rounded-full px-4 py-2 text-sm font-medium text-white/70 transition-colors hover:text-white"
            >
              {t(`home.nav.${link.key}`)}
            </a>
          ))}

          {/* Free Tools dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setToolsOpen(true)}
            onMouseLeave={() => setToolsOpen(false)}
          >
            <button
              type="button"
              onClick={() => setToolsOpen((v) => !v)}
              className="flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium text-white/70 transition-colors hover:text-white"
            >
              {t('nav.free_tools')}
              <ChevronDown
                className={`h-4 w-4 transition-transform ${toolsOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {toolsOpen && (
              <div className="absolute left-0 top-full w-56 pt-2">
                <div className="overflow-hidden rounded-xl border border-white/10 bg-ink/95 p-1 shadow-2xl backdrop-blur-xl">
                  {FREE_TOOLS.map((tool) =>
                    tool.external ? (
                      <a
                        key={tool.key}
                        href={tool.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setToolsOpen(false)}
                        className="block rounded-lg px-3 py-2 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                      >
                        {t(`nav.${tool.key}`)}
                      </a>
                    ) : (
                      <a
                        key={tool.key}
                        href={tool.route}
                        onClick={goRoute(tool.route)}
                        className="block rounded-lg px-3 py-2 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                      >
                        {t(`nav.${tool.key}`)}
                      </a>
                    )
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Products dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setProductsOpen(true)}
            onMouseLeave={() => setProductsOpen(false)}
          >
            <button
              type="button"
              onClick={() => setProductsOpen((v) => !v)}
              className="flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium text-white/70 transition-colors hover:text-white"
            >
              {t('nav.products')}
              <ChevronDown
                className={`h-4 w-4 transition-transform ${productsOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {productsOpen && (
              <div className="absolute left-0 top-full w-56 pt-2">
                <div className="overflow-hidden rounded-xl border border-white/10 bg-ink/95 p-1 shadow-2xl backdrop-blur-xl">
                  {PRODUCTS.map((product) => (
                    <a
                      key={product.key}
                      href={product.route}
                      onClick={goRoute(product.route)}
                      className="block rounded-lg px-3 py-2 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                    >
                      {t(`nav.${product.key}`)}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right actions */}
        <div className="hidden items-center gap-3 md:flex">
          <LanguageSelector />
          <Button
            href={BOOKING_URL}
            external
            variant="primary"
            className="!px-4 !py-2"
            onClick={() => gaEvents.homeBookDemo('navbar')}
          >
            {t('home.nav.cta')}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Mobile toggle */}
        <button
          className="flex h-10 w-10 items-center justify-center rounded-lg text-white md:hidden"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t border-white/10 bg-ink/95 backdrop-blur-xl md:hidden">
          <div className="space-y-1 px-5 py-4">
            {NAV_LINKS.map((link) => (
              <a
                key={link.key}
                href={`/#${link.anchor}`}
                onClick={goToSection(link.anchor)}
                className="block rounded-lg px-3 py-2.5 text-base font-medium text-white/80 hover:bg-white/5 hover:text-white"
              >
                {t(`home.nav.${link.key}`)}
              </a>
            ))}

            {/* Free Tools group */}
            <p className="px-3 pb-1 pt-3 text-xs font-semibold uppercase tracking-wider text-white/40">
              {t('nav.free_tools')}
            </p>
            {FREE_TOOLS.map((tool) =>
              tool.external ? (
                <a
                  key={tool.key}
                  href={tool.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  className="block rounded-lg px-3 py-2.5 text-base font-medium text-white/80 hover:bg-white/5 hover:text-white"
                >
                  {t(`nav.${tool.key}`)}
                </a>
              ) : (
                <a
                  key={tool.key}
                  href={tool.route}
                  onClick={goRoute(tool.route)}
                  className="block rounded-lg px-3 py-2.5 text-base font-medium text-white/80 hover:bg-white/5 hover:text-white"
                >
                  {t(`nav.${tool.key}`)}
                </a>
              )
            )}

            {/* Products group */}
            <p className="px-3 pb-1 pt-3 text-xs font-semibold uppercase tracking-wider text-white/40">
              {t('nav.products')}
            </p>
            {PRODUCTS.map((product) => (
              <a
                key={product.key}
                href={product.route}
                onClick={goRoute(product.route)}
                className="block rounded-lg px-3 py-2.5 text-base font-medium text-white/80 hover:bg-white/5 hover:text-white"
              >
                {t(`nav.${product.key}`)}
              </a>
            ))}

            <div className="flex items-center justify-between pt-3">
              <LanguageSelector dropUp />
              <Button
                href={BOOKING_URL}
                external
                variant="primary"
                className="!px-4 !py-2"
                onClick={() => gaEvents.homeBookDemo('navbar_mobile')}
              >
                {t('home.nav.cta')}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
